import { styled } from "@mui/material/styles";
import { Theme } from "@mui/material/styles";
import CsiFormData from "csi/action/FormData";
import SaveDialog, { SaveDialogProps } from "designSystem/components/dialog/SaveDialog";
import { Context } from "csi/def/Context";
import DojoWidget from "csi/dojoInReact/DojoWidget";
import { debounce } from "lodash";
import React, { FunctionComponent, useEffect, useState } from "react";

const PREFIX = "ReactViewDialog";

const classes = {
	reactViewDialogRoot: `${PREFIX}reactViewDialogRoot`,
	dojoComponent: `${PREFIX}dojoComponent`,
};

const StyledSaveDialog = styled(SaveDialog)((
	{
		theme: Theme,
	},
) => ({
	[`& .${classes.reactViewDialogRoot}`]: {
		margin: 0,
		maxHeight: "100%",
		maxWidth: "100%",
	},

	[`& .${classes.dojoComponent}`]: {
		display: "flex",
	},
}));

// NOTE: This module is not currently being used, but remains here in case we need it in future

//TODO: Test onResize procedure, how it finding right node based on reactViewDialogRoot class, verify if this class is unique in the situation with several dialogs simultaneously

export interface ReactViewDialogProps {
	/** form data */
	formData?: CsiFormData;
	/** width of client windows when we change dialog sizes ratio from 1 to 0.9 
	 * to allow some area around the dialog vs have it occupied the entire view port 
	 * default is 760 */
	fullScreenThreshold: number;
	/** Minimum dialog height (default is 100) */
	minDialogHeight: number;
	/** Minimum dialog width (default is 300) */
	minDialogWidth: number;
	/** Dojo Widget to insert in dialog, can be already created or just declaration to be created */
	widget: any;
	/** Z-index of dialog */
	zIndex?: number;

	onClose: VoidFunction;
	saveDialogProps?: SaveDialogProps;
}

/**
 * Return ratio to use to calculate available dialog space in viewport
 * We have couple of dialogs which require a lot of vertical space: dual tables is a case.
 * For those dialogs we need to consider a situation, when app is running on the device with small screen resolution.
 * Larger dialog requires minimum 681px height. Standard maxRation (defined in DOJO) is 0.9. 681/0.9=757px (rounding up to 760)
 * If our view port is less then 760, then we set maxRation to 1, which will allow the dialog to use all avaialble height.
 * It is also useful for other dialogs, allow to use more space for its needs.
 * @return {Number}
 */
const getViewportRatio = (fullScreenThreshold: number): number => {
	return (window.innerHeight <= fullScreenThreshold) ? 1 : 0.9;
};

const ReactViewDialog: FunctionComponent<ReactViewDialogProps> = (props) => {
	const {
		formData,
		widget,
		onClose,
		saveDialogProps,
		fullScreenThreshold = 760,
		minDialogHeight = 100,
		minDialogWidth = 300,
		zIndex,
	} = props;

	const [calculatedFixedHeight, setFixedHeight] = useState(0);

	// enable disable Save button
	const [btnSaveDisabled, setBtnSaveDisabled] = useState<boolean>(!!formData);

	const onResize = () => {
		const scrollNode = widget.scrollNode;
		const widgetContainerNode = widget.containerNode;
		const containerNode: HTMLElement = document.querySelector("." + classes.reactViewDialogRoot);

		if (scrollNode && containerNode) {
			scrollNode.style.maxWidth = "";
			scrollNode.style.maxHeight = "";
			containerNode.style.overflow = "hidden";

			// scroll node portion of whole dialog sizes
			let fixedHeight = Math.max(containerNode.offsetHeight - scrollNode.offsetHeight, 0);

			// prevent wizard widget resize slowness and flicker
			if (fixedHeight > calculatedFixedHeight) {
				setFixedHeight(fixedHeight);
			} else {
				fixedHeight = calculatedFixedHeight;
			}

			// count dialog padding (same padding left and right)
			const fixedWidth = widgetContainerNode.offsetLeft * 2;

			// total available area and aspect ratio - use all available space on smaller view ports
			const aspectRatio = getViewportRatio(fullScreenThreshold);
			const height = window.innerHeight * aspectRatio;
			const width = window.innerWidth * aspectRatio;

			// scroll node available area
			const maxHeight = Math.max(height - fixedHeight - 2, minDialogHeight);
			const maxWidth = Math.max(width - fixedWidth - 2, minDialogWidth);

			scrollNode.style.maxWidth = maxWidth + "px";
			scrollNode.style.maxHeight = maxHeight + "px";
		} else if (containerNode) {
			containerNode.style.overflow = "auto";
		}
		widget.resize();
	};

	const onResizeDebounced = debounce(onResize);

	useEffect(() => {
		// we want to enable Save button only if there are some changes already done in the form
		formData?.addSelectListener((ctx: Context, url: string, bChecked: boolean, bOld: boolean) => {
			const canEnableSaveButton = !formData.m_bAllowSelections || formData.hasSelections();
			setBtnSaveDisabled(!canEnableSaveButton);
		});

		// listen for windows resize event, as modal Dialog.onResize will be only called upon creation and when dialog size decrease
		window.addEventListener("resize", onResizeDebounced);

		return () => {
			// release listener on component destroy
			window.removeEventListener("resize", onResizeDebounced);
		};
	});

	return (
		<StyledSaveDialog
			// default react dialogs conflicting with DOJO popups: react dialogs enforcing focus, maintaining
			// the watch that focus will never leave dialog or its nested elements
			// @material-ui/core/esm/Unstable_TrapFocus/Unstable_TrapFocus.js
			// react dialog being inserted in HTML at body root level
			// dojo popups grow from different "branch" but also from body level, which means that dojo popups, even thought they visually
			// inserted inside react dialog, actually are inserted to the root level of HTML body
			// as soon as dojo popup receive focus, react detects this and force focus its own popup dialog, 
			// then dojo popup lost focus, trigger onBlur and close
			// disableEnforceFocus - by adding this property we are disabling this react watch and allowing dojo popups to leave
			// (FYI) MUI Trap Focus API: https://mui.com/base/api/trap-focus/
			// disableAutoFocus - this property, according to API documentation, responsible for automatic focus of newly created dialog
			// we used to have it enabled, but this caused issue with nested react dialog not being focused, so I 
			// commented it out but leave here for a while for memory
			//disableAutoFocus
			classes={{ paper: classes.reactViewDialogRoot }}
			disableEnforceFocus
			disableSave={btnSaveDisabled}
			onClose={onClose}
			onResize={onResizeDebounced}
			open
			showClose
			style={zIndex ? { zIndex: zIndex } : {}}
			{...saveDialogProps}
		>
			{{
				main: (
					<DojoWidget className={classes.dojoComponent} widget={widget} />
				),
			}}
		</StyledSaveDialog>
	);
};

export default ReactViewDialog;